/* ============================================================================
   Shop All — client-side filter engine.

   Responsibilities:
   1. Auto-merge additional paginated pages via Section Rendering API so the
      full catalog lives in the DOM (Shopify caps paginate at 50 items; we
      need 100+ items for a true no-reload filter experience).
   2. Product-type single-select filter (tabs).   [M4]
   3. Size multi-select filter (chips).           [M5]
   4. Sort dropdown.                              [M5]
   5. URL state via history.replaceState.         [M6]
   6. Empty state toggle when no cards match.

   The grid is shown/hidden via CSS `display: none` on [hidden] cards — never
   remove DOM nodes, so filters are fully reversible.
   ============================================================================ */
(() => {
  'use strict';

  // --------------------------------------------------------------------------
  // Boot
  // --------------------------------------------------------------------------

  const section = document.querySelector('[data-section-id]');
  if (!section || !section.classList.contains('shopall')) return;

  const grid         = section.querySelector('[data-shopall-grid]');
  const countEl      = section.querySelector('[data-shopall-count]');
  const emptyEl      = section.querySelector('[data-shopall-empty]');
  const clearBtn     = section.querySelector('[data-shopall-clear]');
  const emptyClear   = section.querySelector('[data-shopall-clear-from-empty]');
  const sortSelect   = section.querySelector('[data-shopall-sort]');
  const typeChips    = section.querySelectorAll('[data-shopall-type]');
  const typesTrigger = section.querySelector('[data-shopall-types-trigger]');
  const typesLabel   = section.querySelector('[data-shopall-types-trigger-label]');
  const typesDrawer  = section.querySelector('[data-shopall-types-drawer]');
  const typesBackdrop = section.querySelector('[data-shopall-types-backdrop]');
  const typesClose   = section.querySelector('[data-shopall-types-close]');
  const sizeGroup    = section.querySelector('[data-shopall-sizes-container]');
  const flavorGroup  = section.querySelector('[data-shopall-flavors-container]');
  const benefitGroup = section.querySelector('[data-shopall-benefits-container]');
  const familyGroup  = section.querySelector('[data-shopall-families-container]');
  const bestsellerToggle = section.querySelector('[data-shopall-bestseller-toggle]');

  // Mobile sticky filter bar + sheet (Four Sigmatic-style).
  const mobileBar      = section.querySelector('[data-shopall-mobilebar]');
  const sheet          = section.querySelector('[data-shopall-sheet]');
  const sheetBody      = section.querySelector('[data-shopall-sheet-body]');
  const sheetBackdrop  = section.querySelector('[data-shopall-sheet-backdrop]');
  const countMirror    = section.querySelector('[data-shopall-count-mirror]');
  const activeCountEl  = section.querySelector('[data-shopall-active-filter-count]');

  // Collapsible refinement pill toggles + their panels.
  const filterToggles = section.querySelectorAll('[data-shopall-toggle]');
  const filterPanels  = section.querySelectorAll('[data-shopall-panel]');

  // Size chips ARE server-rendered as of 2026-08-06 (fixed vocab of 10 format+
  // size buckets: 11oz / 500g / 5lb / 30g trial Herbal Coffee, 10ct / 25ct /
  // 50ct Herbal Tea, 100g Instant, 224g Latte, Bundles). They used to be built
  // client-side from each card's raw
  // variant custom.size values, but that vocabulary is inconsistent across the
  // catalog ('11 oz' vs '11 Ounce', '25 Tea Bags' vs '25 Tea bags') and produced
  // duplicate chips in arbitrary order. Each card now emits its bucket keys on
  // data-sizes (see snippets/shop-all-card.liquid), so this is the same simple
  // "card.sizes has-any selected" check the flavor/benefit/family groups use.
  // refreshAvailableSizes() still hides chips no product in the current type
  // offers, so the server can render all 10 unconditionally.
  const sizeChips = sizeGroup
    ? [...sizeGroup.querySelectorAll('[data-shopall-size]')]
    : [];

  // Flavor chips ARE server-rendered (fixed vocab of 7) — just collect refs.
  const flavorChips = flavorGroup
    ? [...flavorGroup.querySelectorAll('[data-shopall-flavor]')]
    : [];

  // Benefit chips ARE server-rendered (fixed vocab of 4) — USDA Organic,
  // Gluten Free, Caffeine Free, Low Caff. Each card emits data-benefits as
  // a space-separated handle list (tags + caffeine-derived states), so
  // filtering is a simple "card.benefits has-any selected" check.
  const benefitChips = benefitGroup
    ? [...benefitGroup.querySelectorAll('[data-shopall-benefit]')]
    : [];

  // Family chips ARE server-rendered (fixed vocab of 5 product lines) — each
  // card emits data-families as a space-separated handle list (tag-derived),
  // so filtering is a simple "card.families has-any selected" check.
  const familyChips = familyGroup
    ? [...familyGroup.querySelectorAll('[data-shopall-family]')]
    : [];

  // BULK MODE (Justin 2026-08-06). An ordered list of size buckets; each card
  // boots showing the FIRST one it actually offers, instead of the product's
  // default variant. Set only when the section's size_mode is 'bulk' (the Bulk
  // Bags collection) — empty everywhere else, so this is a no-op by default.
  //
  // This is a DISPLAY default, deliberately not filter state: state.sizes stays
  // empty, no chip reads as active, and no ?size= lands in the URL. The grid
  // still shows every product; each card just leads with its bulk variant.
  const defaultSizePrefs = (section.dataset.defaultSizePreference || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  // Which bucket should THIS card display? An explicitly-picked chip always
  // wins; otherwise fall back to the first bulk bucket the card carries.
  function resolveCardSize(card, activeSize) {
    if (activeSize) return activeSize;
    if (!defaultSizePrefs.length) return null;
    const have = (card.dataset.sizes || '').split(/\s+/).filter(Boolean);
    return defaultSizePrefs.find((k) => have.includes(k)) || null;
  }

  if (!grid) return;

  // --------------------------------------------------------------------------
  // Filter state
  // --------------------------------------------------------------------------

  const state = {
    type: 'all',
    sizes: new Set(),
    flavors: new Set(),
    benefits: new Set(),
    families: new Set(),
    bestseller: false,
    sort: 'default',
  };

  // --------------------------------------------------------------------------
  // 1) Auto-merge paginated pages (Section Rendering API)
  //    Fetches ?page=N&section_id=<id> for each additional page and appends
  //    the cards into the grid so client-side filters can see every product.
  // --------------------------------------------------------------------------

  // Set once mergeAllPages() finishes, so a ?feature= miss before then is
  // treated as "maybe on a later page", not "not found".
  let pagesMerged = false;

  async function mergeAllPages() {
    let nextUrl = grid.dataset.nextUrl;
    const hasNext = grid.dataset.hasNext === '1';
    if (!hasNext || !nextUrl) return;

    const sectionId = section.dataset.sectionId;
    let safety = 10; // hard stop — catalog is ~140 products, 50/page, so 3 pages max

    while (nextUrl && safety-- > 0) {
      const pageMatch = nextUrl.match(/[?&]page=(\d+)/);
      const pageNum = pageMatch ? pageMatch[1] : null;
      if (!pageNum) break;

      try {
        // Search pages must keep ?q= (and Shopify's ?type= / options), so
        // take the next link as-is there. Collections keep the original
        // pathname-only form.
        let pageUrl = `${window.location.pathname}?page=${pageNum}&section_id=${sectionId}`;
        if (isSearchPage) {
          const u = new URL(nextUrl, window.location.origin);
          u.searchParams.set('section_id', sectionId);
          pageUrl = u.pathname + u.search;
        }
        const res = await fetch(pageUrl, { headers: { 'Accept': 'text/html' } });
        if (!res.ok) break;
        const html = await res.text();

        // Parse the returned section HTML, extract cards from its grid
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = doc.querySelector('[data-shopall-grid]');
        if (!nextGrid) break;

        nextGrid.querySelectorAll('[data-shopall-card]').forEach((card) => {
          grid.appendChild(card);
        });

        // Update next pointer. Shopify's `default_pagination` filter renders
        // the next link as `<span class="next"><a href=...>Next »</a></span>`
        // — the "next" class is on the wrapping span, NOT a rel attr on the
        // anchor. The original `a[rel~="next"]` selector matched nothing, so
        // the loop stopped after page 2 even when page 3+ existed. Match BOTH
        // shapes (the actual Shopify output `.next a` + the rel-based form)
        // so this stays robust if pagination markup changes later.
        const nextLink = doc.querySelector(
          '[data-shopall-pagination] .next a, [data-shopall-pagination] a[rel~="next"]'
        );
        nextUrl = nextLink ? nextLink.getAttribute('href') : null;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[shop-all] page merge failed:', err);
        break;
      }
    }

    updateCount();

    // Re-init the nutrition modal on cards that were appended by the merge.
    // The script's own boot runs at DOMContentLoaded and only sees page-1
    // cards; cards from pages 2/3 carry their own .pdp-nutrition dialog roots
    // and need wiring. pdp-nutrition.js guards against double-init via the
    // data-pdp-nutrition-inited flag, so this is safe.
    if (typeof window.pdpNutritionBoot === 'function') {
      window.pdpNutritionBoot();
    }
  }

  // --------------------------------------------------------------------------
  // 1b) Cross-collection type chips
  //
  //     The type nav is a FIXED global vocabulary (Herbal Coffee, Herbal Tea,
  //     Instant Coffee, Instant Latte, Bundles, Samplers, Accessories) and it
  //     renders identically on every collection that uses this section. The
  //     grid, however, only ever holds `collection.products`. So on a scoped
  //     collection — /collections/herbal-tea, /collections/dandelion, etc —
  //     clicking a chip for a type that collection does not carry filtered to
  //     zero cards and rendered an empty grid (reported 2026-08-28: herbal-tea
  //     + "Herbal Coffee" ‒> ?type=herbal-coffee, 0 products).
  //
  //     Fix: on a scoped collection, a chip with NO matching card in the grid
  //     navigates to the full catalog with that type preselected instead of
  //     filtering in place. Chips that DO match still filter client-side, so
  //     genuine in-collection refinement (e.g. Samplers inside Dandelion) is
  //     untouched. On the catalog itself (handle "all") this is inert — which
  //     is also what prevents a redirect loop.
  //
  //     Safe on paginated collections: init() awaits mergeAllPages() before
  //     hydrate and before any chip is clickable, so the grid is complete by
  //     the time we count.
  // --------------------------------------------------------------------------

  const collectionHandle = section.dataset.collectionHandle || '';
  const catalogUrl       = section.dataset.catalogUrl || '/collections/all';
  const isScopedCollection = collectionHandle !== '' && collectionHandle !== 'all';

  // Search results page (sections/main-search.liquid, 2026-10-28). Same grid,
  // same filters — but the grid holds search.results instead of a collection:
  //  - the product-type chip writes ?ptype= instead of ?type=, because Shopify
  //    search already owns ?type= (product / article / page). A ?type=article
  //    blog-scoped search must never be read as a product-type filter.
  //  - page merges keep ?q= (see mergeAllPages).
  //  - chips with no matching result are hidden (refreshSearchChips).
  const isSearchPage = section.dataset.pageSource === 'search';
  const TYPE_URL_KEY = isSearchPage ? 'ptype' : 'type';

  function typeHasCards(value) {
    if (!value || value === 'all') return true;
    return !!grid.querySelector('[data-shopall-card][data-type="' + value + '"]');
  }

  // True when this type cannot be satisfied here and belongs on the catalog.
  function typeNeedsCatalog(value) {
    return isScopedCollection && !typeHasCards(value);
  }

  function catalogUrlForType(value) {
    return catalogUrl + '?type=' + encodeURIComponent(value);
  }

  // --------------------------------------------------------------------------
  // 2/3) Apply filters + sort
  // --------------------------------------------------------------------------

  function applyFilters() {
    // Reconcile the filter UIs against the current type FIRST so we don't
    // leave orphan "active" chips that point at values not offered by the
    // selected type (e.g. 5lb Bag after switching to Herbal Tea, or a
    // flavor only carried by coffee when Herbal Tea is selected).
    refreshAvailableSizes();
    refreshAvailableFlavors();

    const cards = grid.querySelectorAll('[data-shopall-card]');
    let visibleCount = 0;

    // Only swap to a variant image when exactly ONE size is active. With zero or
    // two+ sizes, there's no single "this size" to show, so we keep the default.
    const activeSize = state.sizes.size === 1 ? [...state.sizes][0] : null;

    cards.forEach((card) => {
      let show = true;

      // Type filter
      if (state.type !== 'all') {
        if (card.dataset.type !== state.type) show = false;
      }

      // Size filter (multi-select OR within group — any match shows the card)
      if (show && state.sizes.size > 0) {
        const cardSizes = (card.dataset.sizes || '').split(/\s+/).filter(Boolean);
        const anyMatch = [...state.sizes].some((s) => cardSizes.includes(s));
        if (!anyMatch) show = false;
      }

      // Flavor filter (multi-select OR within group — any match shows the card)
      if (show && state.flavors.size > 0) {
        const cardFlavors = (card.dataset.flavors || '').split(/\s+/).filter(Boolean);
        const anyMatch = [...state.flavors].some((f) => cardFlavors.includes(f));
        if (!anyMatch) show = false;
      }

      // Benefits filter (multi-select OR within group — any match shows the card)
      // Vocabulary: usda-organic, gluten-free, caffeine-free, low-caff.
      // Caffeine-free + low-caff are mutually exclusive on a single card so
      // OR semantics let "show me anything not over-caffeinated" pick both.
      if (show && state.benefits.size > 0) {
        const cardBenefits = (card.dataset.benefits || '').split(/\s+/).filter(Boolean);
        const anyMatch = [...state.benefits].some((b) => cardBenefits.includes(b));
        if (!anyMatch) show = false;
      }

      // Family filter (multi-select OR within group — any match shows the card)
      // Vocabulary: mediterranean, maya, dandelion, miraculous-mushrooms,
      // prebiotic-superboost. Products with no family tag match no family chip.
      if (show && state.families.size > 0) {
        const cardFamilies = (card.dataset.families || '').split(/\s+/).filter(Boolean);
        const anyMatch = [...state.families].some((f) => cardFamilies.includes(f));
        if (!anyMatch) show = false;
      }

      // Best Sellers toggle — when on, only cards tagged Best Seller remain.
      if (show && state.bestseller) {
        if (card.dataset.bestseller !== 'true') show = false;
      }

      card.hidden = !show;
      if (show) visibleCount++;

      // Image / price / link swap — only needed for visible cards.
      // resolveCardSize lets each card fall back to its own bulk bucket when no
      // chip is picked (bulk mode); returns activeSize unchanged otherwise.
      if (show) swapCardImage(card, resolveCardSize(card, activeSize));
    });

    applySort();
    // Ad-driven ?feature=<handle> keeps slot 1 while Sort = Default
    // (snippets/shop-all-feature-pin.liquid). final=true: pages are merged.
    if (state.sort === 'default' && typeof window.tcShopAllFeature === 'function') {
      window.tcShopAllFeature(section, pagesMerged);
    }
    toggleEmpty(visibleCount);
    updateCount(visibleCount);
    updateFilterBadges();
    toggleClearButton();
    updateURL();
  }

  // --------------------------------------------------------------------------
  //    Variant image swap
  //    When exactly one size is active, each card with a matching variant image
  //    shows that variant's picture and its link lands the PDP on ?variant=<id>.
  //    When the active size changes, clears, or becomes multi, we restore.
  // --------------------------------------------------------------------------

  function getVariantImageMap(card) {
    if (card._variantImageMap !== undefined) return card._variantImageMap;
    const raw = card.dataset.variantImages;
    if (!raw || raw === '{}') {
      card._variantImageMap = null;
      return null;
    }
    try {
      card._variantImageMap = JSON.parse(raw);
    } catch (err) {
      card._variantImageMap = null;
    }
    return card._variantImageMap;
  }

  function getVariantPriceMap(card) {
    if (card._variantPriceMap !== undefined) return card._variantPriceMap;
    const raw = card.dataset.variantPrices;
    if (!raw || raw === '{}') {
      card._variantPriceMap = null;
      return null;
    }
    try {
      card._variantPriceMap = JSON.parse(raw);
    } catch (err) {
      card._variantPriceMap = null;
    }
    return card._variantPriceMap;
  }

  // --------------------------------------------------------------------------
  //    Variant PRICE repaint (Justin 2026-08-06)
  //    Selecting a size swapped the pack shot and the PDP href but left the
  //    default variant's price sitting under it — a 5lb bag priced like an 11oz
  //    bag. This moves price, compare-at strike-through and the campaign % in
  //    lockstep with the image.
  //
  //    INTELLIGEMS SAFETY: Intelligems IS loaded on this page and can rewrite
  //    prices client-side. We therefore capture each node's ORIGINAL text lazily
  //    on the first repaint (from the live DOM, not from a server-rendered data
  //    attribute) and restore exactly that. So if IG has substituted a test
  //    price, clearing the size filter puts IG's value back rather than the
  //    theme's. While a size IS selected we necessarily show the theme's
  //    per-variant price, because IG exposes no per-variant test price to read.
  //    Verified 2026-08-06: IG made zero mutations to these nodes over a 3s
  //    watch, i.e. no price test is currently running on this surface.
  // --------------------------------------------------------------------------

  function repaintCardPrice(card, activeSize) {
    const current = card.querySelector('[data-shopall-price-current]');
    if (!current) return;
    const compare = card.querySelector('[data-shopall-price-compare]');
    const ribbon  = card.querySelector('[data-shopall-campaign-ribbon]');
    const pctEl   = card.querySelector('[data-shopall-campaign-pct]');
    // The on-image "Flavor of the Month · Save X%" badge (shared snippet
    // card-campaign-badge.liquid). It renders the FIRST-AVAILABLE variant's %,
    // so without this it would sit at "Save 26%" over a 5lb price showing 27%.
    const saveEl  = card.querySelector('[data-card-campaign-save]');

    // Lazily snapshot the as-rendered state the first time we touch this card.
    if (card._priceDefaults === undefined) {
      card._priceDefaults = {
        current: current.textContent,
        compare: compare ? compare.textContent : null,
        compareHidden: compare ? compare.hidden : null,
        pct: pctEl ? pctEl.textContent : null,
        ribbonHidden: ribbon ? ribbon.hidden : null,
        save: saveEl ? saveEl.textContent : null,
        saveHidden: saveEl ? saveEl.hidden : null,
      };
    }
    const d = card._priceDefaults;

    let target = null;
    if (activeSize) {
      const map = getVariantPriceMap(card);
      if (map && map[activeSize]) target = map[activeSize];
    }

    if (target) {
      if (current.textContent !== target.price) current.textContent = target.price;
      if (compare) {
        const showCompare = !!target.onSale;
        if (showCompare && compare.textContent !== target.compare) compare.textContent = target.compare;
        if (compare.hidden !== !showCompare) compare.hidden = !showCompare;
      }
      // Campaign ribbon exists only on in-campaign products, and a specific
      // variant may carry no discount at all — hide the ribbon in that case
      // rather than leave a stale "30% OFF" over a full-price size.
      if (ribbon && pctEl) {
        const pct = Number(target.pct) || 0;
        if (pct > 0) {
          const month = ribbon.dataset.promoMonth || '';
          const text = `${pct}% OFF ${month}`.trim();
          if (pctEl.textContent !== text) pctEl.textContent = text;
          if (ribbon.hidden) ribbon.hidden = false;
        } else if (!ribbon.hidden) {
          ribbon.hidden = true;
        }
      }
      if (saveEl) {
        const pct = Number(target.pct) || 0;
        if (pct > 0) {
          const text = `Save ${pct}%`;
          if (saveEl.textContent !== text) saveEl.textContent = text;
          if (saveEl.hidden) saveEl.hidden = false;
        } else if (!saveEl.hidden) {
          saveEl.hidden = true;
        }
      }
    } else {
      if (current.textContent !== d.current) current.textContent = d.current;
      if (compare && d.compare !== null) {
        if (compare.textContent !== d.compare) compare.textContent = d.compare;
        if (compare.hidden !== d.compareHidden) compare.hidden = d.compareHidden;
      }
      if (ribbon && pctEl && d.pct !== null) {
        if (pctEl.textContent !== d.pct) pctEl.textContent = d.pct;
        if (ribbon.hidden !== d.ribbonHidden) ribbon.hidden = d.ribbonHidden;
      }
      if (saveEl && d.save !== null) {
        if (saveEl.textContent !== d.save) saveEl.textContent = d.save;
        if (saveEl.hidden !== d.saveHidden) saveEl.hidden = d.saveHidden;
      }
    }
  }

  function swapCardImage(card, activeSize) {
    // Price moves with the image — same activeSize, one call site.
    repaintCardPrice(card, activeSize);

    const img = card.querySelector('[data-shopall-card-img]');
    const link = card.querySelector('[data-shopall-card-link]');
    if (!img || !link) return;

    const defaultHref = card.dataset.defaultHref;
    const defaultSrc = img.dataset.defaultSrc;
    const defaultSrcset = img.dataset.defaultSrcset;
    const defaultAlt = img.dataset.defaultAlt;

    // Figure out what the desired target is
    let target = null;
    if (activeSize) {
      const map = getVariantImageMap(card);
      if (map && map[activeSize]) target = map[activeSize];
    }

    if (target) {
      if (img.src !== target.src) img.src = target.src;
      if (img.getAttribute('srcset') !== target.srcset) img.setAttribute('srcset', target.srcset);
      if (img.alt !== target.alt) img.alt = target.alt;
      const desiredHref = `${defaultHref}?variant=${target.id}`;
      if (link.getAttribute('href') !== desiredHref) link.setAttribute('href', desiredHref);
    } else {
      // Restore defaults (cheap no-ops if already at default)
      if (defaultSrc && img.src !== defaultSrc) img.src = defaultSrc;
      if (defaultSrcset && img.getAttribute('srcset') !== defaultSrcset) img.setAttribute('srcset', defaultSrcset);
      if (defaultAlt && img.alt !== defaultAlt) img.alt = defaultAlt;
      if (defaultHref && link.getAttribute('href') !== defaultHref) link.setAttribute('href', defaultHref);
    }
  }

  // --------------------------------------------------------------------------
  //    (removed 2026-08-06) buildSizeChips() used to construct the size chips
  //    client-side from each card raw variant custom.size values after the
  //    pagination merge. Size is now a fixed 10-bucket format+size vocabulary
  //    server-rendered in sections/main-shop-all.liquid, so the chips exist at
  //    boot and are wired alongside the flavor/benefit/family groups below.
  // --------------------------------------------------------------------------

  // --------------------------------------------------------------------------
  //    Dynamic size filter
  //    Show only size chips that are offered by at least one product in the
  //    currently-selected type. If the user switches to a type that doesn't
  //    offer a currently-active size (e.g. "5lb" is active and they click
  //    "Herbal Tea"), auto-remove that size from state so the grid doesn't
  //    collapse to empty.
  // --------------------------------------------------------------------------

  function refreshAvailableSizes() {
    const cards = [...grid.querySelectorAll('[data-shopall-card]')];

    // Union of size keys across cards matching the current type filter.
    const available = new Set();
    cards.forEach((card) => {
      if (state.type !== 'all' && card.dataset.type !== state.type) return;
      (card.dataset.sizes || '').split(/\s+/).filter(Boolean).forEach((s) => {
        available.add(s);
      });
    });

    // Show / hide each size chip.
    let visibleChips = 0;
    sizeChips.forEach((chip) => {
      const key = chip.dataset.shopallSize;
      const isAvailable = available.has(key);
      chip.hidden = !isAvailable;
      if (isAvailable) visibleChips++;

      // Auto-deselect a chip that's no longer offered by this type.
      if (!isAvailable && state.sizes.has(key)) {
        state.sizes.delete(key);
        chip.classList.remove('is-active');
      }
    });

    // If no sizes apply to this type at all (e.g. accessories-only), hide the
    // whole filter pill + panel so the label doesn't float alone.
    const sizeToggle = section.querySelector('[data-shopall-toggle="size"]');
    const sizePanel  = section.querySelector('[data-shopall-panel="size"]');
    if (sizeToggle) sizeToggle.hidden = visibleChips === 0;
    // If the pill gets hidden mid-interaction, also collapse its panel.
    if (visibleChips === 0 && sizePanel && !sizePanel.hidden) {
      sizePanel.hidden = true;
      if (sizeToggle) sizeToggle.setAttribute('aria-expanded', 'false');
    }
  }

  // --------------------------------------------------------------------------
  //    Dynamic flavor filter
  //    Flavor chips are server-rendered from a fixed vocab, but not every
  //    flavor is offered by every type. Hide chips that no product in the
  //    current type carries, and auto-deselect any active flavor that's no
  //    longer available (mirrors the size behavior).
  // --------------------------------------------------------------------------

  function refreshAvailableFlavors() {
    if (!flavorChips.length) return;

    const cards = [...grid.querySelectorAll('[data-shopall-card]')];

    const available = new Set();
    cards.forEach((card) => {
      if (state.type !== 'all' && card.dataset.type !== state.type) return;
      (card.dataset.flavors || '').split(/\s+/).filter(Boolean).forEach((f) => {
        available.add(f);
      });
    });

    let visibleChips = 0;
    flavorChips.forEach((chip) => {
      const key = chip.dataset.shopallFlavor;
      const isAvailable = available.has(key);
      chip.hidden = !isAvailable;
      if (isAvailable) visibleChips++;

      if (!isAvailable && state.flavors.has(key)) {
        state.flavors.delete(key);
        chip.classList.remove('is-active');
        chip.setAttribute('aria-pressed', 'false');
      }
    });

    const flavorToggle = section.querySelector('[data-shopall-toggle="flavor"]');
    const flavorPanel  = section.querySelector('[data-shopall-panel="flavor"]');
    if (flavorToggle) flavorToggle.hidden = visibleChips === 0;
    if (visibleChips === 0 && flavorPanel && !flavorPanel.hidden) {
      flavorPanel.hidden = true;
      if (flavorToggle) flavorToggle.setAttribute('aria-expanded', 'false');
    }
  }

  // --------------------------------------------------------------------------
  //    Badge counts on the pill toggles — show how many values are picked
  //    inside each collapsed filter so state is visible without opening.
  // --------------------------------------------------------------------------

  function updateFilterBadges() {
    const sizeBadge     = section.querySelector('[data-shopall-filter-count="size"]');
    const flavorBadge   = section.querySelector('[data-shopall-filter-count="flavor"]');
    const benefitsBadge = section.querySelector('[data-shopall-filter-count="benefits"]');
    const familyBadge   = section.querySelector('[data-shopall-filter-count="family"]');

    if (sizeBadge) {
      const n = state.sizes.size;
      sizeBadge.textContent = n;
      sizeBadge.hidden = n === 0;
    }
    if (flavorBadge) {
      const n = state.flavors.size;
      flavorBadge.textContent = n;
      flavorBadge.hidden = n === 0;
    }
    if (benefitsBadge) {
      const n = state.benefits.size;
      benefitsBadge.textContent = n;
      benefitsBadge.hidden = n === 0;
    }
    if (familyBadge) {
      const n = state.families.size;
      familyBadge.textContent = n;
      familyBadge.hidden = n === 0;
    }
  }

  // --------------------------------------------------------------------------
  //    Collapsible filter panels
  //    Each pill toggle controls the panel with matching data-shopall-panel.
  //    Opening one panel does NOT close the others — users may want size and
  //    flavor open together while refining.
  // --------------------------------------------------------------------------

  function setupFilterToggles() {
    const isSidebar = section.dataset.layoutMode === 'sidebar';
    // Accordion is a DESKTOP-RAIL feature: the panel-under-header interleaving
    // (display:contents + order) only exists in the >=990px CSS. Below that the
    // sidebar collection renders the standard horizontal pill bar, so the
    // accordion would push an open panel's chips below the sort dropdown,
    // detached from its header. Gate accordion behavior to the desktop rail;
    // on mobile keep the original "pre-open all, toggle independently" flow.
    const desktopRail = () => isSidebar && window.matchMedia('(min-width: 990px)').matches;

    // Helper: open/close a single panel and sync its toggle's aria-expanded.
    function setPanel(toggle, open) {
      const key = toggle.dataset.shopallToggle;
      const panel = section.querySelector(`[data-shopall-panel="${key}"]`);
      if (!panel) return;
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    filterToggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const key = toggle.dataset.shopallToggle;
        const panel = section.querySelector(`[data-shopall-panel="${key}"]`);
        if (!panel) return;

        const willOpen = panel.hidden; // about to open if currently hidden

        // Desktop rail = accordion: opening one group collapses the others so
        // the rail stays short and only one chip set is in play at a time.
        // Top layout (and mobile sidebar) keep independent toggles.
        if (desktopRail() && willOpen) {
          filterToggles.forEach((other) => {
            if (other !== toggle) setPanel(other, false);
          });
        }

        setPanel(toggle, willOpen);
      });
    });

    if (desktopRail()) {
      // Desktop accordion init: start with exactly ONE group open (the first
      // visible toggle — Size hydrates async and starts hidden, so this is
      // normally Taste). Tighter rail, products higher above the fold.
      filterToggles.forEach((toggle) => setPanel(toggle, false));
      const firstVisible = [...filterToggles].find((t) => !t.hidden);
      if (firstVisible) setPanel(firstVisible, true);
    } else if (isSidebar) {
      // Mobile sidebar: original behavior — pre-open all panels so the
      // filters are discoverable in the horizontal bar; toggles independent.
      filterToggles.forEach((toggle) => {
        if (!toggle.hidden) setPanel(toggle, true);
      });
    }
  }

  function applySort() {
    const mode = state.sort;
    if (mode === 'default') return; // keep server order

    const cards = [...grid.querySelectorAll('[data-shopall-card]')];

    // Sort on the price the card is actually SHOWING. With a size selected the
    // cards display that variant's price, so keying the sort off the default
    // variant's data-price would order the grid by numbers nobody can see.
    // Falls back to data-price when the card has no entry for this bucket.
    const activeSize = state.sizes.size === 1 ? [...state.sizes][0] : null;
    const priceOf = (card) => {
      // Same resolution the card used to paint itself, so bulk mode sorts by
      // the bulk prices on screen rather than the hidden 11oz defaults.
      const key = resolveCardSize(card, activeSize);
      if (key) {
        const map = getVariantPriceMap(card);
        const cents = map && map[key] && map[key].cents;
        if (typeof cents === 'number') return cents;
      }
      return +card.dataset.price;
    };

    const sorters = {
      'price-asc':    (a, b) => priceOf(a) - priceOf(b),
      'price-desc':   (a, b) => priceOf(b) - priceOf(a),
      'title-asc':    (a, b) => a.dataset.title.localeCompare(b.dataset.title),
      'bestselling':  (a, b) => +b.dataset.created - +a.dataset.created, // proxy: newest-first
    };

    const fn = sorters[mode];
    if (!fn) return;

    cards.sort(fn).forEach((card) => grid.appendChild(card));
  }

  // --------------------------------------------------------------------------
  // 4) Empty state + count
  // --------------------------------------------------------------------------

  function toggleEmpty(visibleCount) {
    if (!emptyEl) return;
    if (visibleCount === 0) {
      emptyEl.hidden = false;
      grid.style.display = 'none';
    } else {
      emptyEl.hidden = true;
      grid.style.display = '';
    }
  }

  function updateCount(visible) {
    // The visible product-count display was removed (Justin 2026-06-05), so
    // countEl may be absent. We still compute the total to drive the mobile
    // active-filter badge via syncMobileBar — don't early-return on no countEl.
    const total = visible !== undefined
      ? visible
      : grid.querySelectorAll('[data-shopall-card]:not([hidden])').length;
    if (countEl) countEl.textContent = total;
    syncMobileBar(total);
  }

  // Mirror the live count into the sheet's "Show N products" button and show a
  // count of active filters on the mobile "Filters" pill.
  function syncMobileBar(total) {
    if (countMirror) countMirror.textContent = (total !== undefined ? total : (countEl ? countEl.textContent : ''));
    if (activeCountEl) {
      let n = 0;
      if (state.type !== 'all') n += 1;
      if (state.bestseller) n += 1;
      n += state.sizes.size + state.flavors.size + state.benefits.size + state.families.size;
      activeCountEl.textContent = n;
      activeCountEl.hidden = n === 0;
    }
  }

  function toggleClearButton() {
    if (!clearBtn) return;
    const hasFilters =
      state.type !== 'all' ||
      state.sizes.size > 0 ||
      state.flavors.size > 0 ||
      state.benefits.size > 0 ||
      state.families.size > 0 ||
      state.bestseller ||
      state.sort !== 'default';
    clearBtn.hidden = !hasFilters;
  }

  // --------------------------------------------------------------------------
  // 5) URL state
  // --------------------------------------------------------------------------

  // Only the filter/sort keys below are OURS. Everything else on the URL —
  // utm_*, gclid, fbclid, msclkid, ?feature=, etc. — is preserved as-is.
  // (Before 2026-10-26 this rebuilt the query string from scratch on every
  // applyFilters(), including the initial one on page load, which stripped ad
  // tracking tags from the address bar and fired a second GA4 page_view with
  // the bare URL.)
  const OWNED_URL_KEYS = [TYPE_URL_KEY, 'size', 'flavor', 'benefit', 'family', 'bestseller', 'sort'];

  function updateURL() {
    const params = new URLSearchParams(window.location.search);
    OWNED_URL_KEYS.forEach((k) => params.delete(k));
    if (state.type !== 'all') params.set(TYPE_URL_KEY, state.type);
    if (state.sizes.size > 0) params.set('size', [...state.sizes].join(','));
    if (state.flavors.size > 0) params.set('flavor', [...state.flavors].join(','));
    if (state.benefits.size > 0) params.set('benefit', [...state.benefits].join(','));
    if (state.families.size > 0) params.set('family', [...state.families].join(','));
    if (state.bestseller) params.set('bestseller', '1');
    if (state.sort !== 'default') params.set('sort', state.sort);

    const qs = params.toString();
    const newURL = (qs
      ? `${window.location.pathname}?${qs}`
      : window.location.pathname) + window.location.hash;
    // No-op when nothing changed, so the page-load pass never emits a
    // history event (GA4 enhanced measurement counts those as page views).
    const current = window.location.pathname + window.location.search + window.location.hash;
    if (newURL === current) return;
    window.history.replaceState(window.history.state, '', newURL);
  }

  function hydrateFromURL() {
    const params = new URLSearchParams(window.location.search);
    const type = params.get(TYPE_URL_KEY);
    const sizes = params.get('size');
    const flavors = params.get('flavor');
    const benefits = params.get('benefit');
    const families = params.get('family');
    const bestseller = params.get('bestseller');
    const sort = params.get('sort');

    if (type) {
      // Deep link / back-button into a scoped collection carrying a type that
      // collection does not stock (shared links from before 2026-08-28 look
      // exactly like this). Replace — not push — so Back skips the dead URL.
      if (typeNeedsCatalog(type)) {
        window.location.replace(catalogUrlForType(type));
        return;
      }
      state.type = type;
      typeChips.forEach((c) => {
        const isActive = c.dataset.shopallType === type;
        c.classList.toggle('is-active', isActive);
        c.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      if (typeof updateTypesTriggerLabel === 'function') updateTypesTriggerLabel();
    }
    if (sizes) {
      // Size is single-select (2026-08-06), so honour only the FIRST value even
      // if an older shared link carries a comma list like ?size=5lb,bundles.
      // Without this clamp a stale URL could restore a multi-selection that the
      // UI can no longer produce or clear one chip at a time.
      const firstSize = sizes.split(',').filter(Boolean)[0];
      if (firstSize) state.sizes.add(firstSize);
      sizeChips.forEach((c) => {
        if (state.sizes.has(c.dataset.shopallSize)) {
          c.classList.add('is-active');
          c.setAttribute('aria-pressed', 'true');
        }
      });
      // Auto-open the size panel on hydrate so the user can see what's active.
      openPanel('size');
    }
    if (flavors) {
      flavors.split(',').forEach((f) => state.flavors.add(f));
      flavorChips.forEach((c) => {
        if (state.flavors.has(c.dataset.shopallFlavor)) {
          c.classList.add('is-active');
          c.setAttribute('aria-pressed', 'true');
        }
      });
      openPanel('flavor');
    }
    if (benefits) {
      benefits.split(',').forEach((b) => state.benefits.add(b));
      benefitChips.forEach((c) => {
        if (state.benefits.has(c.dataset.shopallBenefit)) {
          c.classList.add('is-active');
          c.setAttribute('aria-pressed', 'true');
        }
      });
      openPanel('benefits');
    }
    if (families) {
      families.split(',').forEach((f) => state.families.add(f));
      familyChips.forEach((c) => {
        if (state.families.has(c.dataset.shopallFamily)) {
          c.classList.add('is-active');
          c.setAttribute('aria-pressed', 'true');
        }
      });
      openPanel('family');
    }
    if (bestseller === '1') {
      state.bestseller = true;
      if (bestsellerToggle) {
        bestsellerToggle.classList.add('is-active');
        bestsellerToggle.setAttribute('aria-pressed', 'true');
      }
    }
    if (sort && sortSelect) {
      state.sort = sort;
      sortSelect.value = sort;
    }
  }

  function openPanel(key) {
    const toggle = section.querySelector(`[data-shopall-toggle="${key}"]`);
    const panel  = section.querySelector(`[data-shopall-panel="${key}"]`);
    if (!toggle || !panel) return;
    panel.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
  }

  function closeAllPanels() {
    filterPanels.forEach((p) => { p.hidden = true; });
    filterToggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
  }

  // --------------------------------------------------------------------------
  // 6) Event wiring
  // --------------------------------------------------------------------------

  // --- Mobile types drawer ---
  // Drawer is inert on desktop (CSS hides the trigger + backdrop and renders
  // the nav inline). These handlers only fire meaningfully on mobile.
  let lastFocusBeforeDrawer = null;

  function isDrawerActive() {
    // CSS hides the trigger at >=750px; if it isn't displayed, we're on desktop.
    return typesTrigger && typesTrigger.offsetParent !== null;
  }

  function openTypesDrawer() {
    if (!typesDrawer || !typesBackdrop || !typesTrigger) return;
    lastFocusBeforeDrawer = document.activeElement;
    typesBackdrop.hidden = false;
    typesDrawer.hidden = false;
    // next frame so the transition runs from the hidden state
    requestAnimationFrame(() => {
      typesBackdrop.classList.add('is-open');
      typesDrawer.classList.add('is-open');
    });
    typesTrigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    // Focus the active chip if any, else the close button
    const activeChip = typesDrawer.querySelector('.shopall__type-chip.is-active');
    (activeChip || typesClose || typesDrawer).focus({ preventScroll: true });
  }

  function closeTypesDrawer() {
    if (!typesDrawer || !typesBackdrop || !typesTrigger) return;
    typesBackdrop.classList.remove('is-open');
    typesDrawer.classList.remove('is-open');
    typesTrigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    // Wait for transition, then actually hide so the drawer is removed from
    // the tab order.
    const cleanup = () => {
      typesDrawer.hidden = true;
      typesBackdrop.hidden = true;
      typesDrawer.removeEventListener('transitionend', cleanup);
    };
    typesDrawer.addEventListener('transitionend', cleanup);
    // Fallback in case transitionend doesn't fire (prefers-reduced-motion etc.)
    setTimeout(cleanup, 400);
    if (lastFocusBeforeDrawer && lastFocusBeforeDrawer.focus) {
      lastFocusBeforeDrawer.focus({ preventScroll: true });
    }
  }

  function updateTypesTriggerLabel() {
    if (!typesLabel) return;
    const active = section.querySelector('.shopall__type-chip.is-active');
    if (!active) return;
    const labelText = active.querySelector('span')?.textContent?.trim() || 'All';
    typesLabel.textContent = labelText;
    // Accent the trigger border to match the selected type (except "all")
    const type = active.dataset.shopallType;
    if (type && type !== 'all' && typesTrigger) {
      const accent = active.style.getPropertyValue('--chip-accent');
      typesTrigger.style.borderColor = accent || '';
    } else if (typesTrigger) {
      typesTrigger.style.borderColor = '';
    }
  }

  if (typesTrigger) {
    typesTrigger.addEventListener('click', () => {
      if (typesDrawer && typesDrawer.classList.contains('is-open')) {
        closeTypesDrawer();
      } else {
        openTypesDrawer();
      }
    });
  }
  if (typesBackdrop) typesBackdrop.addEventListener('click', closeTypesDrawer);
  if (typesClose) typesClose.addEventListener('click', closeTypesDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && typesDrawer && typesDrawer.classList.contains('is-open')) {
      closeTypesDrawer();
    }
  });

  // Focus trap inside the drawer while open
  if (typesDrawer) {
    typesDrawer.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || !typesDrawer.classList.contains('is-open')) return;
      const focusables = typesDrawer.querySelectorAll(
        'button, [href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  typeChips.forEach((chip) => {
    chip.addEventListener('click', () => {
      const value = chip.dataset.shopallType;

      // Type not present in THIS collection — send the shopper to the catalog
      // with it preselected rather than filtering the grid down to nothing.
      if (typeNeedsCatalog(value)) {
        window.location.assign(catalogUrlForType(value));
        return;
      }

      state.type = value;
      typeChips.forEach((c) => {
        const isActive = c === chip;
        c.classList.toggle('is-active', isActive);
        c.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      updateTypesTriggerLabel();
      if (isDrawerActive() && typesDrawer && typesDrawer.classList.contains('is-open')) {
        closeTypesDrawer();
      }
      applyFilters();
    });
  });

  // Size chips ARE server-rendered as of 2026-08-06 (fixed 10-bucket vocab), so
  // they wire at boot exactly like flavor/benefit/family — no post-merge
  // construction step. data-shopall-size matches card.data-sizes bucket keys.
  //
  // SINGLE-SELECT (Justin 2026-08-06) — unlike Family / Taste / Dietary Needs,
  // only ONE size may be active at a time: picking a second replaces the first.
  // Clicking the active chip still clears it, so "no size" stays reachable
  // without hunting for Clear filters. state.sizes remains a Set (every
  // consumer — the filter test, the badge, updateURL, refreshAvailableSizes —
  // is written against a Set and keeps working) but now holds 0 or 1 entry.
  //
  // Kept aria-pressed toggle semantics rather than switching to role="radio":
  // a radio group cannot be emptied, and deselecting back to "all sizes" is a
  // state we explicitly support.
  //
  // Side benefit: applyFilters() only swaps a card to its variant pack shot
  // when exactly one size is active, so that swap now fires on every pick
  // instead of only when the user happened to have a single chip selected.
  function selectSize(chip) {
    const value = chip.dataset.shopallSize;
    const wasActive = state.sizes.has(value);

    // Clear the whole group first — this is what makes it single-select.
    state.sizes.clear();
    sizeChips.forEach((c) => {
      c.classList.remove('is-active');
      c.setAttribute('aria-pressed', 'false');
    });

    if (!wasActive) {
      state.sizes.add(value);
      chip.classList.add('is-active');
      chip.setAttribute('aria-pressed', 'true');
    }
    applyFilters();
  }

  sizeChips.forEach((chip) => {
    chip.addEventListener('click', () => selectSize(chip));
  });

  // Flavor chips ARE server-rendered, so we can wire them at boot.
  flavorChips.forEach((chip) => {
    chip.addEventListener('click', () => {
      const value = chip.dataset.shopallFlavor;
      if (state.flavors.has(value)) {
        state.flavors.delete(value);
        chip.classList.remove('is-active');
        chip.setAttribute('aria-pressed', 'false');
      } else {
        state.flavors.add(value);
        chip.classList.add('is-active');
        chip.setAttribute('aria-pressed', 'true');
      }
      applyFilters();
    });
  });

  // Benefit chips — same wiring pattern as flavor chips (server-rendered,
  // fixed vocab of 4, multi-select toggle). data-shopall-benefit handle on
  // each chip matches the values stamped into card.data-benefits.
  benefitChips.forEach((chip) => {
    chip.addEventListener('click', () => {
      const value = chip.dataset.shopallBenefit;
      if (state.benefits.has(value)) {
        state.benefits.delete(value);
        chip.classList.remove('is-active');
        chip.setAttribute('aria-pressed', 'false');
      } else {
        state.benefits.add(value);
        chip.classList.add('is-active');
        chip.setAttribute('aria-pressed', 'true');
      }
      applyFilters();
    });
  });

  // Family chips — same wiring pattern as flavor/benefit chips (server-rendered,
  // fixed vocab of 5, multi-select toggle). data-shopall-family handle on each
  // chip matches the values stamped into card.data-families.
  familyChips.forEach((chip) => {
    chip.addEventListener('click', () => {
      const value = chip.dataset.shopallFamily;
      if (state.families.has(value)) {
        state.families.delete(value);
        chip.classList.remove('is-active');
        chip.setAttribute('aria-pressed', 'false');
      } else {
        state.families.add(value);
        chip.classList.add('is-active');
        chip.setAttribute('aria-pressed', 'true');
      }
      applyFilters();
    });
  });

  // Best Sellers standalone toggle — single boolean filter.
  if (bestsellerToggle) {
    bestsellerToggle.addEventListener('click', () => {
      state.bestseller = !state.bestseller;
      bestsellerToggle.classList.toggle('is-active', state.bestseller);
      bestsellerToggle.setAttribute('aria-pressed', state.bestseller ? 'true' : 'false');
      applyFilters();
    });
  }

  if (sortSelect) {
    sortSelect.addEventListener('change', (e) => {
      state.sort = e.target.value;
      applyFilters();
    });
  }

  function clearAll() {
    state.type = 'all';
    state.sizes.clear();
    state.flavors.clear();
    state.benefits.clear();
    state.families.clear();
    state.bestseller = false;
    state.sort = 'default';

    typeChips.forEach((c) => {
      const isActive = c.dataset.shopallType === 'all';
      c.classList.toggle('is-active', isActive);
      c.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    updateTypesTriggerLabel();
    sizeChips.forEach((c) => {
      c.classList.remove('is-active');
      c.setAttribute('aria-pressed', 'false');
    });
    flavorChips.forEach((c) => {
      c.classList.remove('is-active');
      c.setAttribute('aria-pressed', 'false');
    });
    benefitChips.forEach((c) => {
      c.classList.remove('is-active');
      c.setAttribute('aria-pressed', 'false');
    });
    familyChips.forEach((c) => {
      c.classList.remove('is-active');
      c.setAttribute('aria-pressed', 'false');
    });
    if (bestsellerToggle) {
      bestsellerToggle.classList.remove('is-active');
      bestsellerToggle.setAttribute('aria-pressed', 'false');
    }
    if (sortSelect) sortSelect.value = 'default';

    closeAllPanels();
    applyFilters();
  }

  if (clearBtn) clearBtn.addEventListener('click', clearAll);
  if (emptyClear) emptyClear.addEventListener('click', clearAll);

  // --------------------------------------------------------------------------
  // Init
  // --------------------------------------------------------------------------

  // --------------------------------------------------------------------------
  //    Mobile sticky filter/sort bar + sheet (Four Sigmatic-style)
  //    <990px: relocate the type nav + refine controls into a bottom sheet so
  //    the page top isn't buried under filters; a fixed bar opens the sheet.
  //    >=990px: controls return to their original spots (the sidebar rail).
  // --------------------------------------------------------------------------
  function setupMobileSheet() {
    if (!sheet || !sheetBody || !mobileBar) return;

    const typesNav = section.querySelector('.shopall__types');
    const refine   = section.querySelector('.shopall__refine');
    // Remember each control's original home so we can put it back on desktop.
    const homes = [typesNav, refine]
      .filter(Boolean)
      .map(function (el) { return { el: el, parent: el.parentNode, next: el.nextElementSibling }; });

    var mq = window.matchMedia('(max-width: 989px)');
    var lastFocus = null;

    function place() {
      if (mq.matches) {
        // Move into the sheet (type first, then refine) — only if not already.
        if (typesNav && typesNav.parentNode !== sheetBody) sheetBody.appendChild(typesNav);
        if (refine && refine.parentNode !== sheetBody) sheetBody.appendChild(refine);
      } else {
        homes.forEach(function (h) {
          if (h.el.parentNode === sheetBody) {
            if (h.next && h.next.parentNode === h.parent) h.parent.insertBefore(h.el, h.next);
            else h.parent.appendChild(h.el);
          }
        });
      }
    }

    function openSheet(scrollToSort) {
      lastFocus = document.activeElement;
      sheetBackdrop.hidden = false;
      sheet.hidden = false;
      requestAnimationFrame(function () {
        sheetBackdrop.classList.add('is-open');
        sheet.classList.add('is-open');
      });
      document.body.style.overflow = 'hidden';
      var closeBtn = sheet.querySelector('[data-shopall-sheet-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
      if (scrollToSort) {
        var sortWrap = refine && refine.querySelector('.shopall__sort-wrap, [data-shopall-sort]');
        if (sortWrap) setTimeout(function () { sortWrap.scrollIntoView({ block: 'center' }); }, 80);
      }
    }

    function closeSheet() {
      if (sheet.hidden) return;
      sheetBackdrop.classList.remove('is-open');
      sheet.classList.remove('is-open');
      document.body.style.overflow = '';
      var done = function () {
        sheet.hidden = true;
        sheetBackdrop.hidden = true;
        sheet.removeEventListener('transitionend', done);
      };
      sheet.addEventListener('transitionend', done);
      setTimeout(done, 400);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    place();
    if (mq.addEventListener) mq.addEventListener('change', function () { closeSheet(); place(); });

    var openF = section.querySelector('[data-shopall-open-filters]');
    var openS = section.querySelector('[data-shopall-open-sort]');
    var closeB = section.querySelector('[data-shopall-sheet-close]');
    var applyB = section.querySelector('[data-shopall-sheet-apply]');
    if (openF) openF.addEventListener('click', function () { openSheet(false); });
    if (openS) openS.addEventListener('click', function () { openSheet(true); });
    if (closeB) closeB.addEventListener('click', closeSheet);
    if (applyB) applyB.addEventListener('click', closeSheet);
    sheetBackdrop.addEventListener('click', closeSheet);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && !sheet.hidden) closeSheet(); });

    // Focus trap inside the sheet.
    sheet.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || sheet.hidden) return;
      var f = [].slice.call(sheet.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  // Search only: hide every type / family / dietary chip that no result
  // carries, so "matcha" never offers an Accessories tab that empties the grid.
  // Size + Taste chips already self-hide (refreshAvailableSizes/Flavors).
  function refreshSearchChips() {
    if (!isSearchPage) return;
    const cards = [...grid.querySelectorAll('[data-shopall-card]')];
    const has = (attr, key) => cards.some((c) =>
      (c.dataset[attr] || '').split(/\s+/).includes(key));
    typeChips.forEach((chip) => {
      const key = chip.dataset.shopallType;
      if (key && key !== 'all') chip.hidden = !cards.some((c) => c.dataset.type === key);
    });
    [['family', 'families', 'shopallFamily'], ['benefits', 'benefits', 'shopallBenefit']].forEach(([panelKey, attr, chipKey]) => {
      const panel = section.querySelector('[data-shopall-panel="' + panelKey + '"]');
      const toggle = section.querySelector('[data-shopall-toggle="' + panelKey + '"]');
      if (!panel) return;
      let visible = 0;
      panel.querySelectorAll('button').forEach((chip) => {
        const ok = has(attr, chip.dataset[chipKey]);
        chip.hidden = !ok;
        if (ok) visible++;
      });
      if (toggle) toggle.hidden = visible === 0;
    });
  }

  (async function init() {
    setupFilterToggles();  // wire pill toggle → panel visibility (safe pre-merge)
    setupMobileSheet();    // relocate filters into the mobile bottom sheet
    await mergeAllPages();
    pagesMerged = true;
    refreshSearchChips();
    hydrateFromURL();   // needs chips in the DOM to apply .is-active
    applyFilters();
    syncMobileBar();    // initial count mirror + active-filter badge
  })();
})();
